import { getAppStartTime, getCurrentTimeInMs } from 'lib/DatetimeManager';
import {
  DESTINATION_GROUP,
  NAVIGATION_ORIGIN,
  NAVIGATION_TRIGGER_TYPE,
  SEARCHLIGHT_EVENT_TYPE,
} from 'root/lib/SearchlightDispatcher';
import Env from 'lib/Env';
import { PRODUCT_LECTURA, GAME_MODE, USAGE_TAGS } from 'lib/constants';
import { setRumQuestId, setView, setRumGame } from 'lib/DDRum';
import { updateUsageTags } from 'a/actions/appSessionActions';
import { getCurrentQuestId, getIsChallengeQuestComplete, getIsQuestWorkflow } from 'a/hub/selectors';
import { getLocalization } from 'a/selectors/languageSelectors';
import goToActivity from 'a/actions/navigationActions';
import { reportNavigation } from 'a/hub/actions/metagameSearchlight';
import { getBenchmarkEOYPath } from 'a/placementTool/redux/selectors';

import createActionObj from '../../actions/createActionObj';
import gameIds from '../data/GameIds';
import questState from '../reducers/state/questState';
import { SEASON, REFRESH_TIMEOUT, APP_REFRESH_LIMIT } from '../data/constants';

export const createMetaGameActionObj = (type, payload) =>
  createActionObj(gameIds.METAGAME, type, payload);

export function setCurriculumVersions(curriculumVersions) {
  return createMetaGameActionObj('SET_CURRICULUM_VERSIONS', { curriculumVersions });
}

export const goToMetaGameScreen = () => (dispatch, getState) => {
  const urlData = new URLSearchParams(window.location.search);
  const {
    curiosoReducer: {
      introComplete,
    },
    questWorkflowReducer: {
      season,
    },
    metaGameReducer: {
      currentQuest: {
        questLocation,
        questNumberIndex: questIndex,
      },
    },
  } = getState();

  if (introComplete) {
    if (questIndex && questIndex !== 0) {
      const navigationParams = {
        destinationGroup: DESTINATION_GROUP.QUEST,
        origin: NAVIGATION_ORIGIN.MAP,
        trigger: 'backButton',
        triggerType: NAVIGATION_TRIGGER_TYPE.AUTOMATIC,
      };
      goToQuest({
        questLocation,
        questNumberIndex: questIndex,
        navigationParams,
      });
    } else if (season === SEASON.GRADE_K_1 || season === SEASON.GRADE_2_3) {
      const questId = urlData.get('questId');
      if (questId) {
        const navigationParams = {
          destinationGroup: DESTINATION_GROUP.QUEST,
          origin: NAVIGATION_ORIGIN.EXTERNAL_APP,
          trigger: 'redirect',
          triggerType: NAVIGATION_TRIGGER_TYPE.AUTOMATIC,
        };
        if (questState.questsOrder[`quest${questId}`]) {
          const { location } = questState.questsOrder[`quest${questId}`];
          dispatch(
            goToQuest({
              questLocation: location,
              questNumberIndex: parseInt(questId, 10),
              isNewQuest: true,
              navigationParams,
            }),
          );
        } else {
          dispatch(goToHubScreen({ screenName: 'Map', navigationParams }));
        }
      } else {
        dispatch(goToHubScreen({ screenName: 'Map' }));
      }
    } else {
      const activity = urlData.get('activity');
      if (activity) {
        if (activity.toLowerCase() === 'practice') {
          const navigationParams = {
            destinationGroup: DESTINATION_GROUP.PRACTICE,
            origin: NAVIGATION_ORIGIN.EXTERNAL_APP,
            trigger: 'redirect',
            triggerType: NAVIGATION_TRIGGER_TYPE.AUTOMATIC,
          };
          dispatch(goToHubScreen({ screenName: 'PracticePage', navigationParams }));
          window.history.replaceState('', '', Env.MAIN_APP_URL);
          return;
        }
      }
      dispatch(goToHubScreen({ screenName: 'Map' }));
    }
    window.history.replaceState('', '', Env.MAIN_APP_URL);
  } else {
    if (season === SEASON.GRADE_4_5) {
      const activity = urlData.get('activity');
      if (activity) {
        if (activity.toLowerCase() === 'practice') {
          const navigationParams = {
            destinationGroup: DESTINATION_GROUP.PRACTICE,
            origin: NAVIGATION_ORIGIN.EXTERNAL_APP,
            trigger: 'redirect',
            triggerType: NAVIGATION_TRIGGER_TYPE.AUTOMATIC,
          };
          dispatch(goToHubScreen({ screenName: 'PracticePage', navigationParams }));
          window.history.replaceState('', '', Env.MAIN_APP_URL);
          return;
        }
      }
    }
    // Should be 'FirstTime' but for launch we will always go to Map
    dispatch(goToHubScreen({ screenName: 'Map' }));
    window.history.replaceState('', '', Env.MAIN_APP_URL);
  }
};

export const showLoadingScreen = () => createMetaGameActionObj('SHOW_LOADING_SCREEN');

export const goToHubScreen = ({
  screenName,
  categoryName = '',
  screenParams = {},
  navigationParams = {},
  ignoreReload = false,
}) =>
  (dispatch, getState) => {
    const { questWorkflowReducer: { isQuestInDemoMode } } = getState();
    if (Object.entries(navigationParams).length) {
      dispatch(reportNavigation({ destination: screenName, ...navigationParams }));
    }
    if (
      !ignoreReload
    && !screenParams.showAllQuestsCompletePopup
    && screenName === 'Map'
    && getCurrentTimeInMs() - getAppStartTime() > APP_REFRESH_LIMIT
    ) {
      // move app in loading screen
      dispatch(showLoadingScreen());
      // reload app to free memory
      setTimeout(() => {
        window.location.reload(true);
      }, REFRESH_TIMEOUT); // delay is needed for all possible POSTs to be launched before refresh
    } else {
      dispatch(updateUsageTags({
        [USAGE_TAGS.LOCATION.prefix]: screenName.includes('Quest') ? gameIds.HUB : screenName,
        [USAGE_TAGS.GAME_MODE.prefix]: isQuestInDemoMode ? USAGE_TAGS.GAME_MODE.value[GAME_MODE.DEMO] : null,
        [USAGE_TAGS.LEVEL.prefix]: null,
        [USAGE_TAGS.GAME_PART.prefix]: null,
        [USAGE_TAGS.QUEST_TYPE.prefix]: null,
        [USAGE_TAGS.SKILL_ID.prefix]: null,
        [USAGE_TAGS.ASSESSMENT.prefix]: null,
        [USAGE_TAGS.BOOK_TITLE.prefix]: null,
      }));
      dispatch(createMetaGameActionObj(SEARCHLIGHT_EVENT_TYPE.SELECT_HUB_SCREEN, {
        categoryName,
        screenName,
        screenParams,
      }));
    }
  };

export const goToQuest = ({ questLocation, questNumberIndex, isNewQuest = false, navigationParams = {} }) =>
  (dispatch) => {
    if (Object.entries(navigationParams).length) {
      dispatch(reportNavigation({ destination: questLocation, ...navigationParams }));
    }

    dispatch(updateUsageTags({ [USAGE_TAGS.LOCATION.prefix]: gameIds.HUB }));
    dispatch(createMetaGameActionObj('GO_TO_QUEST', {
      location: questLocation,
      questNumberIndex,
      isNewQuest,
    }));
  };

export const setIntroComplete = () => (dispatch) => {
  const setIntroCompleteAction = createMetaGameActionObj('SET_INTRO_COMPLETE');
  dispatch(setIntroCompleteAction);
};

export const setCuriosoAugmentations = attributes =>
  createMetaGameActionObj('SET_CURIOSO_AUGMENTATION_SECTION', attributes);

export const setGameLaunchedFrom = payload => (dispatch) => {
  if (['PracticePage', 'Practice'].includes(payload?.location)) {
    dispatch(updateUsageTags({ [USAGE_TAGS.GAME_MODE.prefix]: USAGE_TAGS.GAME_MODE.value[GAME_MODE.PRACTICE] }));
  }
  dispatch(createMetaGameActionObj('SET_GAME_LAUNCHED_FROM', payload));
};

export const hideOverlay = attributes =>
  createMetaGameActionObj('HIDE_OVERLAY', attributes);

export const showDemoSelector = payload =>
  createMetaGameActionObj('SHOW_DEMO_SELECTOR', payload);

export const hideDemoSelector = payload =>
  createMetaGameActionObj('HIDE_DEMO_SELECTOR', payload);

export const showDemoToolbar = payload =>
  createMetaGameActionObj('SHOW_DEMO_TOOLBAR', payload);

export const hideDemoToolbar = payload =>
  createMetaGameActionObj('HIDE_DEMO_TOOLBAR', payload);

export const showSelectorOnMTGPopup = payload =>
  createMetaGameActionObj('SHOW_MTG_SELECTOR', payload);

export const openInitialActivity = (product) => (dispatch, getState) => {
  const state = getState();
  const {
    persistStateReducer: { grade, redirectToIpt },
    placementTool: { isBenchmarkModeOn },
    questWorkflowReducer: { captions, isQuestInDemoMode, season },
    skillScan: { redirectToSkillScan },
  } = state;
  const benchmarkEOYPath = getBenchmarkEOYPath(state);
  const redirectToBenchmark = isBenchmarkModeOn || !!benchmarkEOYPath;
  const currentQuestId = getCurrentQuestId(state);
  const localization = getLocalization(state);

  const navigationParams = {
    destinationGroup: DESTINATION_GROUP.PLACEMENT_TOOL,
    origin: NAVIGATION_ORIGIN.LOGIN,
    trigger: 'login',
    triggerType: NAVIGATION_TRIGGER_TYPE.AUTOMATIC,
  };
  const tagsToUpdate = {
    [USAGE_TAGS.GRADE.prefix]: USAGE_TAGS.GRADE.value[grade] ?? null,
    [USAGE_TAGS.SEASON.prefix]: season ?? null,
    [USAGE_TAGS.APP.prefix]: USAGE_TAGS.APP.value[product],
    [USAGE_TAGS.PROGRAM.prefix]: USAGE_TAGS.PROGRAM.value[product],
    [USAGE_TAGS.LANGUAGE.prefix]: USAGE_TAGS.LANGUAGE.value[localization],
    [USAGE_TAGS.CAPTIONS.prefix]: captions ? USAGE_TAGS.CAPTIONS.value : null,
  };

  setRumQuestId(currentQuestId);

  // Check for redirectToIpt (placement tool) flag. If not present, go to main app.
  if (isQuestInDemoMode) {
    // in demo mode we want bypass APT and SkillScan
    navigationParams.destinationGroup = DESTINATION_GROUP.HUB;
    tagsToUpdate[USAGE_TAGS.LOCATION.prefix] = DESTINATION_GROUP.HUB;
    tagsToUpdate[USAGE_TAGS.GAME_MODE.prefix] = USAGE_TAGS.GAME_MODE.value[GAME_MODE.DEMO];
    dispatch(updateUsageTags(tagsToUpdate));
    return dispatch(
      goToActivity({
        activityName: gameIds.HUB,
        isQuestWorkflow: true,
        navigationParams,
      }),
    );
  }

  if (redirectToIpt) {
    tagsToUpdate[USAGE_TAGS.ASSESSMENT.prefix] = USAGE_TAGS.ASSESSMENT.value[gameIds.PLACEMENT_TOOL];
    dispatch(updateUsageTags(tagsToUpdate));
    return dispatch(
      goToActivity({
        activityName: gameIds.PLACEMENT_TOOL,
        isQuestWorkflow: true,
        navigationParams,
      }),
    );
  }

  // TODO: remove '&& product !== PRODUCT_LECTURA' once we have a benchmark for the lectura product
  if (redirectToBenchmark && product !== PRODUCT_LECTURA) {
    navigationParams.destinationGroup = DESTINATION_GROUP.TRO_BENCHMARK;
    tagsToUpdate[USAGE_TAGS.ASSESSMENT.prefix] = USAGE_TAGS.ASSESSMENT.value[gameIds.TRO_BENCHMARK];
    dispatch(updateUsageTags(tagsToUpdate));
    return dispatch(
      goToActivity({
        activityName: gameIds.TRO_BENCHMARK,
        isQuestWorkflow: true,
        navigationParams,
      }),
    );
  }

  if (redirectToSkillScan) {
    navigationParams.destinationGroup = DESTINATION_GROUP.SKILL_SCAN;
    tagsToUpdate[USAGE_TAGS.ASSESSMENT.prefix] = USAGE_TAGS.ASSESSMENT.value[gameIds.SKILL_SCAN];
    dispatch(updateUsageTags(tagsToUpdate));
    return dispatch(
      goToActivity({
        activityName: gameIds.SKILL_SCAN,
        isQuestWorkflow: true,
        navigationParams,
      }),
    );
  }

  navigationParams.destinationGroup = DESTINATION_GROUP.HUB;
  tagsToUpdate[USAGE_TAGS.LOCATION.prefix] = DESTINATION_GROUP.HUB;
  dispatch(updateUsageTags(tagsToUpdate));
  return dispatch(
    goToActivity({
      activityName: gameIds.HUB,
      navigationParams,
    }),
  );
};

export const reportRumView = (activity) => (_, getState) => {
  const { selectedActivity: { params } } = getState();
  const view = activity === gameIds.E_READER ? params?.game || activity : activity;

  if (activity === gameIds.HUB) {
    setRumGame(null);
  }

  setView(view);
};

export const exitGame = (navigationParams) => (dispatch, getState) => {
  const state = getState();
  const { categoryId } = state.metaGameReducer.gameLaunchedFrom;
  const isQuestWorkflow = getIsQuestWorkflow(state);
  const { location = 'Cafe', questId = '1' } = state.selectedActivity.params;
  const { season } = state.questWorkflowReducer;
  const isChallengeQuestComplete = getIsChallengeQuestComplete(state);
  if (isQuestWorkflow && season !== SEASON.GRADE_4_5) {
    if (questId === 100) {
      return dispatch(goToHubScreen({
        screenName: isChallengeQuestComplete ? 'Curioso' : 'Map',
        navigationParams,
      }));
    }
    return dispatch(goToHubScreen({
      screenName: `${location}Quest${questId}`,
      navigationParams,
    }));
  }
  // if Game is launched from a category that means they're coming from Games List
  // and should return there. Otherwise return to Quest
  if (categoryId !== '') {
    if (season === SEASON.GRADE_4_5) {
      return dispatch(goToHubScreen({
        screenName: 'PracticePage',
        categoryName: categoryId,
        navigationParams,
      }));
    }
    return dispatch(goToHubScreen({
      screenName: 'Curioso',
      screenParams: { selectedTab: 5 },
      categoryName: categoryId,
      navigationParams,
    }));
  }

  if (season === SEASON.GRADE_4_5) {
    return dispatch(goToHubScreen({ screenName: 'Map', navigationParams }));
  }
  return dispatch(goToHubScreen({ screenName: 'Quest', navigationParams }));
};
